import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/common/breadcrumbs'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Content, Page } from '@/components/layout/wrappers'
import { OpportunityContent } from '@/components/opportunity/opportunityOverview/opportunityContent'
import { OpportunityHeader } from '@/components/opportunity/opportunityOverview/opportunityHeader'
import { TxPanel } from '@/components/opportunity/txPanel/txPanel'
import { ALL_SDK_NETWORKS } from '@/config/query/queryParams'
import { routes } from '@/config/routes'
import { useAccountContext } from '@/context/account'
import { useTransactionsContextQuery } from '@/queries/transactionsContext.query'
import { cn } from '@/utils/cn'
import { getDefiSdk } from '@/utils/defiSdk'
import { getOpportunityMeta } from '@/utils/meta/getOpportunityMeta'
import { getLastConnectedAddress } from '@/utils/wagmi/getLastConnectedAddress'
import { formatDate, fromUnixTimestamp } from '@vaultsfyi/common/utils'
import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'
import { zeroAddress } from 'viem'
import { z } from 'zod'

const opportunityPageParamsSchema = z.object({
  network: z.enum(ALL_SDK_NETWORKS),
  vaultId: z.string(),
})

export async function loader({ params, request }: LoaderFunctionArgs) {
  const parseResult = opportunityPageParamsSchema.safeParse(params)
  if (!parseResult.success) {
    throw new Response(null, { status: 404, statusText: 'Not Found' })
  }

  const { network, vaultId } = parseResult.data

  const defiSdk = getDefiSdk()
  const initialTransactionsContextAddress = getLastConnectedAddress(request) ?? zeroAddress
  const [vault, initialTransactionsContext] = await Promise.allSettled([
    defiSdk.getVault({ path: { network, vaultId } }),
    defiSdk.getTransactionsContext({
      path: { userAddress: initialTransactionsContextAddress, network, vaultId },
    }),
  ])

  if (vault.status !== 'fulfilled') {
    throw new Response(null, { status: 404, statusText: 'Not Found' })
  }

  const transactionsContext =
    initialTransactionsContext.status === 'fulfilled' ? initialTransactionsContext.value : undefined

  return { vault: vault.value, initialTransactionsContext: transactionsContext, initialTransactionsContextAddress }
}

export type OpportunityLoader = typeof loader

export const meta: MetaFunction<OpportunityLoader> = ({ data, location }) =>
  getOpportunityMeta({ vault: data?.vault, pathname: location.pathname })

export default function Index() {
  const { vault, initialTransactionsContext, initialTransactionsContextAddress } = useLoaderData<OpportunityLoader>()
  const { name, lastUpdateTimestamp } = vault
  const { account } = useAccountContext()
  const {
    data: transactionsContext,
    isError,
    isFetching,
    refetch,
  } = useTransactionsContextQuery({
    address: account,
    vault,
    initialData: initialTransactionsContext,
    initialDataAddress: initialTransactionsContextAddress,
  })

  return (
    <Page>
      <Breadcrumb className="hidden lg:flex">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to={routes.root} title="Discover opportunities">
              Discover
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <ScrollArea className="h-full">
        <Content className={cn('after:block after:h-22 lg:after:hidden')}>
          <OpportunityHeader vault={vault} />
          <div className="mt-4 flex flex-col lg:grid lg:grid-cols-[1fr_24rem] lg:gap-8">
            <div className="flex flex-col gap-4 overflow-hidden lg:grid lg:h-fit lg:max-w-full">
              <OpportunityContent vault={vault} address={account} transactionsContext={transactionsContext} />
            </div>
            <TxPanel
              key={`${vault.network.name}:${vault.vaultId}`}
              vault={vault}
              transactionsContext={transactionsContext}
              walletAddress={account}
              isContextError={isError}
              isContextFetching={isFetching}
              onRetryContext={() => void refetch()}
            />
          </div>
          <p className="lg:-mb-3 mt-4 text-sm leading-none">
            Data last updated: {formatDate({ timestamp: fromUnixTimestamp(lastUpdateTimestamp), showTime: true })}
          </p>
        </Content>
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </Page>
  )
}
